import React, { useState } from 'react';

import { useMutation } from '@apollo/client/react';
import { c } from 'ttag';

import {
    Alert,
    Button,
    Input,
    Modal,
    ModalContent,
    ModalFooter,
    RadioGroup,
    useNotifications,
} from '@freelancelabs/components';

import RefusalNotice from '../../components/RefusalNotice';
import { DeclarePartnerListDocument, PartnerListsDocument } from '../../graphql/__generated__/types';
import EligiblePartnerPicker, { PickedPartner } from './EligiblePartnerPicker';
import { partnerListRefusalOf } from './refusals';

interface Props {
    onClose: () => void;
}

type Display = 'nominative' | 'anonymous';

const DeclarePartnerListDialog = ({ onClose }: Props) => {
    const { createNotification } = useNotifications();
    const [name, setName] = useState('');
    const [display, setDisplay] = useState<Display | null>(null);
    const [picked, setPicked] = useState<PickedPartner[]>([]);

    const [declare, { loading, error }] = useMutation(DeclarePartnerListDocument, {
        refetchQueries: [PartnerListsDocument],
        awaitRefetchQueries: true,
    });

    const trimmed = name.trim();
    const canSend = trimmed.length > 0 && display !== null && picked.length > 0;

    const submit = async () => {
        if (!canSend) {
            return;
        }
        try {
            await declare({
                variables: {
                    name: trimmed,
                    isAnonymous: display === 'anonymous',
                    sourcePartnerIds: picked.map((partner) => partner.sourcePartnerId),
                },
            });
            createNotification({
                type: 'success',
                text: c('Partner lists').t`List « ${trimmed} » declared`,
            });
            onClose();
        } catch {
            // Rendered from `error` below; the modal stays open on what was typed.
        }
    };

    return (
        <Modal open onClose={onClose} title={c('Partner lists').t`Declare a partner list`} size={760}>
            <ModalContent>
                <div className="flex flex-col gap-5">
                    <div className="flex flex-col gap-1.5">
                        <label htmlFor="partner-list-name" className="text-sm font-medium">
                            {c('Partner lists').t`List name`}
                        </label>
                        <Input id="partner-list-name" value={name} onValue={setName} placeholder="Partenaires IA" />
                    </div>

                    <div className="flex flex-col gap-1.5">
                        <span className="text-sm font-medium">{c('Partner lists').t`Partner display`}</span>
                        <RadioGroup
                            category="card"
                            name="partner-list-display"
                            value={display ?? undefined}
                            onValue={(value: string) => setDisplay(value as Display)}
                            options={[
                                { value: 'nominative', label: c('Partner lists').t`Nominative` },
                                { value: 'anonymous', label: c('Partner lists').t`Anonymous` },
                            ]}
                        />
                    </div>

                    <div className="flex flex-col gap-1.5">
                        <span className="text-sm font-medium">{c('Partner lists').t`How the list is filled`}</span>
                        <p className="text-sm">{c('Partner lists')
                            .t`Manual: a selection among the eligible partners.`}</p>
                    </div>

                    <EligiblePartnerPicker
                        picked={picked}
                        onPicked={setPicked}
                        searchPlaceholder={c('Partner lists').t`Search a partner`}
                        emptyTitle={c('Partner lists').t`No partner is eligible`}
                        emptyDescription={c('Partner lists')
                            .t`Eligibility is recomputed at every read from the Salesforce mirror. An empty referential means the mirror answered nothing, not that the network is empty: tell the support desk rather than declaring an empty list.`}
                    />

                    {trimmed.length > 0 && display !== null && picked.length === 0 && (
                        <Alert type="info">
                            {c('Partner lists').t`A list is never declared empty: pick at least one partner above.`}
                        </Alert>
                    )}

                    {error && <RefusalNotice refusal={partnerListRefusalOf(error)} />}
                </div>
            </ModalContent>
            <ModalFooter>
                <Button category="weak" onClick={onClose} disabled={loading}>
                    {c('Partner lists').t`Cancel`}
                </Button>
                <Button onClick={submit} disabled={!canSend} loading={loading}>
                    {c('Partner lists').t`Declare the list`}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default DeclarePartnerListDialog;
